import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { faDoorOpen } from '@fortawesome/pro-solid-svg-icons';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router';
import styled from 'styled-components';

import {
  AveragePlayerScore,
  Position,
} from '@sorare/core/src/__generated__/globalTypes';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { BodyM, LabelS } from '@sorare/core/src/atoms/typography';
import {
  MainAsideLayout,
  Section,
} from '@sorare/core/src/components/layout/MainAsideLayout';
import { PlayerScore } from '@sorare/core/src/components/scoring/PlayerScore';
import { Manager } from '@sorare/core/src/components/seo/Team/Manager';
import { Squad as DumbSquad } from '@sorare/core/src/components/seo/Team/Squad';
import { ANY_SPORT_PLAYERS_SLUG } from '@sorare/core/src/constants/__generated__/routes';
import { useScreenSize } from '@sorare/core/src/hooks/device/useScreenSize';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { useDefaultAverageScore } from '@sorare/core/src/hooks/useDefaultAverageScore';
import { useTitleAndDescription } from '@sorare/core/src/hooks/useTitleAndDescription';
import { FILTERS } from '@sorare/core/src/lib/filters';
import {
  getAppearancePercentage,
  playableFootballPositions,
  positionNames,
} from '@sorare/core/src/lib/players';
import { generateSportPath } from '@sorare/core/src/lib/routing/generateSportPath';
import { metadatas } from '@sorare/core/src/lib/seo/football';

import type {
  ClubSquadQuery,
  ClubSquadQueryVariables,
} from './__generated__/page.graphql.ts';

const CLUB_SQUAD_QUERY = gql`
  query ClubSquadQuery($slug: String!) {
    football {
      club(slug: $slug) {
        slug
        name
        activeMemberships(first: 100) {
          nodes {
            id
            startDate
            aggregatedStats {
              id
              appearances
            }
            player {
              slug
              sport
              displayName
              avatarPictureUrl: pictureUrl(derivative: "avatar")
              position
              age
              country {
                slug
                code
                threeLetterCode
              }
              ...getAppearancePercentage_anyPlayerInterface
              ...PlayerScore_anyPlayer
            }
          }
        }
      }
    }
  }
  ${PlayerScore.fragments.anyPlayer}
  ${getAppearancePercentage.fragments.anyPlayer}
` as TypedDocumentNode<ClubSquadQuery, ClubSquadQueryVariables>;

const StyledVertical = styled(Vertical)`
  align-items: end;
`;

type Membership =
  ClubSquadQuery['football']['club']['activeMemberships']['nodes'][number];

const Squad = () => {
  const { slug } = useParams();
  const { up: isLaptopAndAbove } = useScreenSize('laptop');
  const { formatMessage } = useIntl();
  const defaultAverageScore = useDefaultAverageScore();
  const { data, loading } = useQuery(CLUB_SQUAD_QUERY, {
    variables: { slug: slug || '' },
    skip: !slug,
  });

  useTitleAndDescription(
    metadatas.clubSquad.title,
    metadatas.clubSquad.description,
    {
      club: data?.football.club.name,
    }
  );

  const membershipsByPosition = useMemo(() => {
    return (data?.football.club.activeMemberships.nodes.reduce(
      (acc, membership) => ({
        ...acc,
        [membership.player.position]: [
          ...(acc[membership.player.position] || []),
          membership,
        ],
      }),
      {} as Record<Position, Membership[]>
    ) || {}) as Record<Position, Membership[]>;
  }, [data?.football.club.activeMemberships.nodes]);

  if (loading) return <LoadingIndicator fullHeight />;
  if (!data) return null;

  const coach = membershipsByPosition[Position.Coach]?.[0];

  const blocks = {
    squad: (
      <DumbSquad
        displayName={data.football.club.name}
        stats={['l5', 'l15', 'played']}
        headers={{
          l5: FILTERS.lastFiveAverageScore.alias,
          l15: FILTERS.lastFifteenAverageScore.alias,
          played: <FontAwesomeIcon icon={faDoorOpen} />,
        }}
        groups={[
          ...playableFootballPositions.filter(
            position => !!membershipsByPosition[position]
          ),
        ]
          .reverse()
          .map(position => ({
            label: formatMessage(positionNames[position]),
            players: membershipsByPosition[position as Position]
              .sort(
                (a, b) =>
                  b.aggregatedStats.appearances - a.aggregatedStats.appearances
              )
              .map(membership => ({
                ...membership.player,
                pictureUrl: membership.player.avatarPictureUrl,
                to: generateSportPath(ANY_SPORT_PLAYERS_SLUG, {
                  params: {
                    slug: membership.player.slug,
                  },
                  sport: membership.player.sport,
                }),
                stats: {
                  l5: (
                    <StyledVertical gap={0.5}>
                      <PlayerScore
                        player={membership.player}
                        mode={AveragePlayerScore.LAST_FIVE_SO5_AVERAGE_SCORE}
                      />
                      <LabelS as="p" color="var(--c-nd-600)">
                        {getAppearancePercentage(
                          AveragePlayerScore.LAST_FIVE_SO5_AVERAGE_SCORE,
                          membership.player
                        )}
                        %
                      </LabelS>
                    </StyledVertical>
                  ),
                  l15: (
                    <StyledVertical gap={0.5}>
                      <PlayerScore
                        player={membership.player}
                        mode={defaultAverageScore}
                      />
                      <LabelS as="p" color="var(--c-nd-600)">
                        {getAppearancePercentage(
                          defaultAverageScore,
                          membership.player
                        )}
                        %
                      </LabelS>
                    </StyledVertical>
                  ),
                  played: (
                    <BodyM as="p">
                      {membership.aggregatedStats.appearances}
                    </BodyM>
                  ),
                },
              })),
          }))}
      />
    ),
    manager: !!coach && (
      <Manager
        displayName={data.football.club.name}
        manager={{
          displayName: coach.player.displayName,
          pictureUrl: coach.player.avatarPictureUrl,
          country: coach.player.country,
          startDate: coach.startDate,
        }}
      />
    ),
  };

  if (isLaptopAndAbove) {
    return (
      <MainAsideLayout aside={[blocks.manager]}>{blocks.squad}</MainAsideLayout>
    );
  }

  return (
    <Section>
      {blocks.manager}
      {blocks.squad}
    </Section>
  );
};

export default Squad;
